import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Target, Users, TrendingUp } from 'lucide-react';
import { adminAPI } from '../services/api';
import StatCard from '../components/StatCard';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';

const Clubs = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['clubStats'],
    queryFn: adminAPI.getClubStats,
  });

  if (isLoading) {
    return <LoadingSpinner text="Loading club statistics..." />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Clubs</h1>
        <p className="text-sm sm:text-base text-text-secondary mt-1">Overview of club statistics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          title="Total Clubs"
          value={data?.total || 0}
          icon={Target}
          gradient={true}
        />
        <StatCard
          title="Avg Members"
          value={data?.avgMembers || 0}
          icon={Users}
        />
        <StatCard
          title="Most Active Club"
          value={data?.mostActive?.[0]?.memberCount || 0}
          subtitle={data?.mostActive?.[0]?.name || 'N/A'}
          icon={TrendingUp}
        />
      </div>

      {/* Member Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Club Size Distribution</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-elevated border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Size Range
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Number of Clubs
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data?.memberDistribution?.map((dist, index) => (
                  <tr key={index} className="hover:bg-background-elevated transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-text-primary">
                        {dist._id === '500+' ? '500+' : `${dist._id} - ${index < data.memberDistribution.length - 2 ? data.memberDistribution[index + 1]._id : '500+'} members`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary">{dist.count}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Most Active Clubs */}
      <Card>
        <CardHeader>
          <CardTitle>Most Active Clubs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-elevated border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Club Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Members
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data?.mostActive?.map((club, index) => (
                  <tr key={club.id} className="hover:bg-background-elevated transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-text-primary">#{index + 1}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Target size={16} className="text-primary" />
                        <span className="text-sm font-medium text-text-primary">
                          {club.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Users size={16} className="text-cyan" />
                        <span className="text-sm text-text-primary">{club.memberCount}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Clubs;
